import type { TagVariant } from '../../shared/ui';

/**
 * Event lifecycle values as serialized by the API (`JsonStringEnumConverter`).
 * MUST stay in sync with the backend `EventStatus` / `EventRegistrationStatus` /
 * `EventKind` enums — see the API contract appendix of `docs/events-feature-plan.md`.
 */
export const EventStatuses = {
  Draft: 'Draft',
  Published: 'Published',
  Full: 'Full',
  Cancelled: 'Cancelled',
  Completed: 'Completed',
} as const;

export type EventStatusValue = (typeof EventStatuses)[keyof typeof EventStatuses];

export const EventRegistrationStatuses = {
  /**
   * Between "register" and the Stripe webhook. Transitory, but it counts against capacity and
   * it is visible to the venue on purpose: a paid event's list would otherwise look emptier
   * than it is for the minute a player spends on the card sheet.
   */
  PendingPayment: 'PendingPayment',
  Confirmed: 'Confirmed',
  Withdrawn: 'Withdrawn',
  Removed: 'Removed',
  /** A confirmed place whose payment never settled — the sweep released it. Nobody decided anything. */
  Expired: 'Expired',
} as const;

export type EventRegistrationStatusValue =
  (typeof EventRegistrationStatuses)[keyof typeof EventRegistrationStatuses];

/**
 * `EventRegistration.refundStatus` values as serialized by the API — see the backend
 * `EventRegistrationRefundState`. Null (absent) means there is nothing to tell.
 */
export const EventRefundStates = {
  Refunded: 'Refunded',
  RefundFailed: 'RefundFailed',
  NotRefunded: 'NotRefunded',
} as const;

export type EventRefundStateValue = (typeof EventRefundStates)[keyof typeof EventRefundStates];

/**
 * Maps a refund state to an `app-tag` variant. `RefundFailed` is the alarm the whole column
 * exists for; `NotRefunded` is a fact to notice, not an error; a completed refund is neutral —
 * green would read as "good news" on a row that is usually a cancellation.
 */
export function eventRefundTagVariant(state: string): TagVariant {
  switch (state) {
    case EventRefundStates.RefundFailed:
      return 'danger';
    case EventRefundStates.NotRefunded:
      return 'warning';
    default:
      return 'neutral';
  }
}

/** The same mapping for the shared responsive-table pills, so phone and desktop agree. */
export function eventRefundPillClass(state: string): string {
  switch (state) {
    case EventRefundStates.RefundFailed:
      return 'rt-pill--danger';
    case EventRefundStates.NotRefunded:
      return 'rt-pill--warn';
    default:
      return '';
  }
}

/**
 * What an event *is*. Chosen once, at creation, and frozen afterwards: the tournament options
 * decide how capacity is counted and how a registration is priced, so a kind that could change
 * later would mean re-interpreting registrations that were already taken.
 *
 * `Class` is a coached group session. It has no options block of its own — it is a Generic
 * event under a different name — so nothing but the label and the filter reads it.
 */
export const EventKinds = {
  Generic: 'Generic',
  Class: 'Class',
  Tournament: 'Tournament',
} as const;

export type EventKindValue = (typeof EventKinds)[keyof typeof EventKinds];

/** Display order of the kind picker and of the list's kind filter. */
export const EVENT_KIND_ORDER: readonly EventKindValue[] = [
  EventKinds.Generic,
  EventKinds.Class,
  EventKinds.Tournament,
];

/**
 * Publishing is the one-way door the publish modal explains: it is offered only while the
 * event is still a draft, because every later status is already published.
 */
export const PUBLISHABLE_STATUSES: readonly string[] = [EventStatuses.Draft];

/** Cancelling is offered from any state that is still running — never from a terminal one. */
export const CANCELLABLE_STATUSES: readonly string[] = [
  EventStatuses.Draft,
  EventStatuses.Published,
  EventStatuses.Full,
];

/**
 * Archiving is a tidy-up action for events that are over, so it is only offered on terminal
 * statuses — the same rule tournaments use, and the same rule the backend guards.
 */
export const ARCHIVABLE_EVENT_STATUSES: readonly string[] = [
  EventStatuses.Completed,
  EventStatuses.Cancelled,
];

/** Details and the form may still be corrected while the event has not ended. */
export const EDITABLE_STATUSES: readonly string[] = [
  EventStatuses.Draft,
  EventStatuses.Published,
  EventStatuses.Full,
];

/** Maps a status to a shared responsive-table pill variant (`rt-pill--*`). */
export function eventStatusPillClass(status: string): string {
  switch (status) {
    case EventStatuses.Published:
      return 'rt-pill--ok';
    case EventStatuses.Draft:
      return 'rt-pill--info';
    case EventStatuses.Full:
      return 'rt-pill--warn';
    case EventStatuses.Cancelled:
      return 'rt-pill--danger';
    default:
      return '';
  }
}

/** Maps a registration status to a shared responsive-table pill variant. */
export function eventRegistrationPillClass(status: string): string {
  switch (status) {
    case EventRegistrationStatuses.Confirmed:
      return 'rt-pill--ok';
    // Not a problem, but not a seat the venue can count on either — it reads as "waiting".
    case EventRegistrationStatuses.PendingPayment:
      return 'rt-pill--warn';
    case EventRegistrationStatuses.Removed:
      return 'rt-pill--danger';
    default:
      return '';
  }
}

/**
 * Statuses whose holder is still in the event, and so still has a registration the venue can
 * act on. A player who has already withdrawn is not removed a second time.
 */
export const ACTIONABLE_REGISTRATION_STATUSES: readonly string[] = [
  EventRegistrationStatuses.Confirmed,
  EventRegistrationStatuses.PendingPayment,
];

/**
 * Maps a status to an `app-tag` variant. Lives here beside the `rt-pill--*` mapping so the
 * desktop pill and the mobile pill of the same status can never drift apart, and so the list
 * and the detail screen agree on what "Full" looks like.
 */
export function eventStatusTagVariant(status: string): TagVariant {
  switch (status) {
    case EventStatuses.Published:
    // Over and done with is still a good outcome — the same green, not a third colour.
    case EventStatuses.Completed:
      return 'success';
    case EventStatuses.Full:
      return 'warning';
    case EventStatuses.Cancelled:
      return 'danger';
    // Draft: not a state to worry about, so the colourless pill.
    default:
      return 'neutral';
  }
}

/** Maps a registration status to an `app-tag` variant. */
export function eventRegistrationTagVariant(status: string): TagVariant {
  switch (status) {
    case EventRegistrationStatuses.Confirmed:
      return 'success';
    // Not a problem, but not a seat the venue can count on either — it reads as "waiting".
    case EventRegistrationStatuses.PendingPayment:
      return 'warning';
    case EventRegistrationStatuses.Removed:
      return 'danger';
    // Withdrawn: the player left of their own accord; nothing is wrong.
    default:
      return 'neutral';
  }
}

/**
 * What kind of thing this is, as a tag. A fact about the event, never a warning about it:
 * a tournament is worth noticing (`info`); a plain event or a class is not (`neutral`).
 */
export function eventKindTagVariant(kind: string): TagVariant {
  return kind === EventKinds.Tournament ? 'info' : 'neutral';
}
